import {
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import BrandIcon from "./BrandIcon";
import AttachButton from "./AttachButton";
import AttachmentPreviews from "./AttachmentPreviews";
import { tokens } from "../theme";
import type { WidgetFeatures } from "../types/api";
import { DEFAULT_FEATURES } from "../lib/configHelpers";
import { MAX_ATTACHMENTS, canSendWith } from "../lib/attachments";
import type { AttachmentNotice } from "../hooks/useAttachments";
import type { ComposerAttachment } from "../types/chat";

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** From GET /v1/config.features — MVP keeps false flags hidden */
  features?: WidgetFeatures;
  /** Files picked via the attach button (shown only when features.file_upload) */
  onAttach?: (files: File[]) => void;
  attachments?: ComposerAttachment[];
  onRemoveAttachment?: (id: string) => void;
  onRetryAttachment?: (id: string) => void;
  attachmentNotice?: AttachmentNotice | null;
}

/** Actions-row layout: text grows up to this, then scrolls. */
const FIELD_MAX_HEIGHT = 160;

const styles: Record<string, CSSProperties> = {
  footer: {
    flexShrink: 0,
    padding: `${tokens.space[3]} ${tokens.space[4]} 14px`,
    background: tokens.surface.canvas,
  },
  // Border lives on the wrapper so image previews sit inside the box.
  fieldWrap: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.lg,
    background: tokens.surface.input,
    boxSizing: "border-box",
  },
  // Longhands only — a `padding` shorthand would wipe the strip's bottom gap.
  previews: {
    paddingTop: 12,
    paddingLeft: 12,
    paddingRight: 12,
  },
  field: {
    flex: 1,
    width: "100%",
    minHeight: 100,
    maxHeight: 200,
    resize: "none",
    border: "none",
    borderRadius: tokens.radius.lg,
    padding: "14px 16px",
    fontSize: tokens.font.size.md,
    fontFamily: "inherit",
    lineHeight: 1.4,
    color: tokens.text.primary,
    background: "transparent",
    outline: "none",
    boxSizing: "border-box" as const,
  },
  fieldWithSend: {
    paddingRight: 52,
  },
  // With attachments on, buttons get their own row below the text so long
  // text scrolls inside the field instead of running under them.
  fieldWithActions: {
    // flex: 1 would make the column ignore the JS-set height.
    flex: "none",
    minHeight: 56,
    maxHeight: FIELD_MAX_HEIGHT,
    paddingBottom: 6,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 44,
    padding: "0 8px 8px 12px",
  },
  sendBtnInRow: {
    position: "static",
    marginLeft: "auto",
  },
  sendBtn: {
    position: "absolute",
    right: 8,
    bottom: 8,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: tokens.gradient.accent,
    color: tokens.text.onBrand,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: tokens.shadow.send,
    padding: 0,
  },
  powered: {
    margin: "18px 0 0",
    textAlign: "center" as const,
    fontSize: tokens.font.size.xs,
    color: tokens.text.muted,
    fontWeight: tokens.font.weight.medium,
  },
};

export default function ChatInput({
  onSend,
  disabled,
  placeholder,
  features = DEFAULT_FEATURES,
  onAttach,
  attachments = [],
  onRemoveAttachment,
  onRetryAttachment,
  attachmentNotice,
}: ChatInputProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const canSend = canSendWith(value, attachments) && !disabled;
  const resolvedPlaceholder = placeholder ?? t("input.placeholder");

  const submit = () => {
    if (!canSend) return;
    onSend(value.trim());
    setValue("");
    const el = fieldRef.current;
    if (el) {
      el.style.height = "";
      el.style.overflowY = "";
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  // Actions-row layout only: grow with the text up to maxHeight, then scroll.
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
    if (!features.file_upload) return;
    const el = event.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, FIELD_MAX_HEIGHT)}px`;
    el.style.overflowY =
      el.scrollHeight > FIELD_MAX_HEIGHT ? "auto" : "hidden";
  };

  const sendButton = (extra?: CSSProperties) => (
    <button
      type="submit"
      style={{ ...styles.sendBtn, ...extra }}
      aria-label={t("input.send")}
    >
      <BrandIcon
        name="sendWhite"
        size={18}
        style={{
          position: "relative",
          top: -1.5,
          left: 1.5,
        }}
      />
    </button>
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <footer
      style={styles.footer}
      data-feature-voice={features.voice ? "on" : "off"}
      data-feature-upload={features.file_upload ? "on" : "off"}
      data-feature-pre-chat={features.pre_chat_form ? "on" : "off"}
    >
      <form onSubmit={handleSubmit}>
        <div style={styles.fieldWrap}>
          {onRemoveAttachment && (
            <AttachmentPreviews
              attachments={attachments}
              onRemove={onRemoveAttachment}
              onRetry={onRetryAttachment}
              notice={attachmentNotice}
              style={styles.previews}
            />
          )}
          <textarea
            ref={fieldRef}
            style={{
              ...styles.field,
              ...(features.file_upload
                ? styles.fieldWithActions
                : canSend
                  ? styles.fieldWithSend
                  : null),
              opacity: disabled ? 0.6 : 1,
            }}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            rows={1}
            aria-label={t("input.ariaLabel")}
          />
          {features.file_upload ? (
            <div style={styles.actions}>
              <AttachButton
                onAttach={onAttach}
                disabled={disabled}
                full={attachments.length >= MAX_ATTACHMENTS}
                remaining={MAX_ATTACHMENTS - attachments.length}
              />
              {canSend && sendButton(styles.sendBtnInRow)}
            </div>
          ) : (
            canSend && sendButton()
          )}
        </div>
      </form>
      {/* Always shown in Beta (BR-12); branding.show_powered_by applies from GA tiers. */}
      <p style={styles.powered}>{t("input.poweredBy")}</p>
    </footer>
  );
}
